/**
 * Insights.jsx
 * Two small panels that only ever show what has actually been researched.
 *
 * Hashtag figures come from Instagram's own hashtag search, which is rationed,
 * and competitor figures from a nightly pull. Neither is inferred: a tag that
 * has never been looked up is absent rather than estimated, and the card says
 * so rather than showing an empty list with no explanation.
 *
 * Dependencies: helpers.js, components/Icons.jsx
 */

import { useState } from 'react';
import { Eye, Hash, TrendDown, TrendUp, Users } from '../Icons.jsx';
import { PLATFORMS, ago, compact, full } from './helpers.js';

const TABS = [
  { key: 'hashtags', label: 'Top hashtags' },
  { key: 'competitors', label: 'Competitors' },
];

/** How hard a tag is to rank for, as the lookup graded it. */
const COMPETITIVENESS = {
  low: { label: 'Reachable', tone: 'ok' },
  medium: { label: 'Competitive', tone: 'warn' },
  high: { label: 'Crowded', tone: 'danger' },
};

function Hashtags({ rows, navigate }) {
  if (!rows.length) {
    return (
      <div className="insight-empty">
        <p>No hashtags have been looked up yet.</p>
        <button className="btn btn--sm" onClick={() => navigate('/hashtags')}>
          <Hash size={15} /> Research a hashtag
        </button>
      </div>
    );
  }

  return (
    <ul className="insight-list">
      {rows.map((row) => {
        const grade = COMPETITIVENESS[row.competitiveness] ?? null;
        return (
          <li key={row.tag}>
            <button type="button" onClick={() => navigate('/hashtags')}>
              <span className="insight-list__name">#{row.tag}</span>
              <span className="insight-list__foot">
                <span className="insight-list__value">
                  {compact(row.median_engagement)} <small>median engagement</small>
                </span>
                {grade && <span className={`badge badge--${grade.tone}`}>{grade.label}</span>}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Competitors({ rows, navigate }) {
  if (!rows.length) {
    return (
      <div className="insight-empty">
        <p>No competitors are being tracked yet.</p>
        <button className="btn btn--sm" onClick={() => navigate('/competitors')}>
          <Users size={15} /> Track an account
        </button>
      </div>
    );
  }

  return (
    <ul className="insight-list">
      {rows.map((row) => {
        // Only ever a real difference between two real readings.
        const moved =
          row.followers_week_ago != null ? row.followers - row.followers_week_ago : null;
        const Arrow = moved > 0 ? TrendUp : TrendDown;
        const meta = PLATFORMS[row.platform];

        return (
          <li key={row.id}>
            <button type="button" onClick={() => navigate('/competitors')}>
              <span className="insight-list__name">
                {meta && <meta.Icon size={13} />}
                {row.label || `@${row.username}`}
              </span>
              <span className="insight-list__foot">
                <span className="insight-list__value">
                  {compact(row.followers)} <small>followers</small>
                </span>
                {moved !== null && moved !== 0 ? (
                  <span className={`stat__delta stat__delta--${moved > 0 ? 'good' : 'bad'}`}>
                    <Arrow size={13} />
                    {moved > 0 ? '+' : ''}
                    {full(moved)}
                  </span>
                ) : (
                  <span className="row__sub">{ago(row.last_checked_at) || 'Not checked yet'}</span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default function Insights({ hashtags = [], competitors = [], navigate }) {
  const [tab, setTab] = useState('hashtags');

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Eye size={21} />
        </span>
        <div className="card__head-text">
          <h2>Research</h2>
          <p>What your hashtags and the accounts you watch are doing.</p>
        </div>
      </div>

      <div className="tab-row" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            type="button"
            aria-selected={tab === t.key}
            className={`tab${tab === t.key ? ' is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'hashtags' ? (
        <Hashtags rows={hashtags} navigate={navigate} />
      ) : (
        <Competitors rows={competitors} navigate={navigate} />
      )}
    </section>
  );
}
